import React, { ChangeEvent, Dispatch, Fragment, MouseEvent, SetStateAction, useEffect, useState } from 'react';

import { Funnel, SlidersHorizontal, X } from 'lucide-react';

import { ICompleteDataFilters } from '../../../types/misc.interface';
import { ESimulationKey } from '../../../types/entities.enum';

interface HeaderFiltersProps {
    filters: ICompleteDataFilters;
    setFilters: Dispatch<SetStateAction<ICompleteDataFilters>>;
    simulations: { key: ESimulationKey, name: string }[];
};
export const HeaderFilters = (props: HeaderFiltersProps) => {
    const { filters, setFilters, simulations } = props;

    const [tFilters, setTFilters] = useState<ICompleteDataFilters>({ start_date: '', end_date: '', scores: [], simulation_keys: [] });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterErrors, setFilterErrors] = useState<string[]>([]);

    const INVALID_DATE_ERROR = 'Invalid date';
    const START_DATE_BEFORE_END_DATE_ERROR = 'Start date must be before end date';

    useEffect(() => {
        setTFilters(filters);
    }, [filters]);

    const onFiltersOpen = () => {
        setFiltersOpen(true);
    };

    const onFiltersClose = () => {
        setTFilters(filters);
        setFiltersOpen(false);
    };

    const onDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const key = e.target.name as keyof ICompleteDataFilters;
        const { value } = e.target;

        setTFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        setFilterErrors(prev => prev.filter((error) => error !== INVALID_DATE_ERROR));
        setFilterErrors(prev => prev.filter((error) => error !== START_DATE_BEFORE_END_DATE_ERROR));

        const isValidDate = (date: string) => {
            return !isNaN(new Date(date).getTime());
        };

        // check if the start date is valid date
        if (tFilters.start_date && !isValidDate(tFilters.start_date) && !filterErrors.includes(INVALID_DATE_ERROR)) {
            setFilterErrors(prev => [...prev, INVALID_DATE_ERROR]);
        };

        // check if the end date is valid date
        if (tFilters.end_date && !isValidDate(tFilters.end_date) && !filterErrors.includes(INVALID_DATE_ERROR)) {
            setFilterErrors(prev => [...prev, INVALID_DATE_ERROR]);
        };

        // check if start date is after end date
        if (tFilters.start_date && tFilters.end_date) {
            const cStart = new Date(tFilters.start_date);
            const cEnd = new Date(tFilters.end_date);
            if (cStart > cEnd && !filterErrors.includes(START_DATE_BEFORE_END_DATE_ERROR)) {
                setFilterErrors(prev => [...prev, START_DATE_BEFORE_END_DATE_ERROR]);
            };
        };

        // eslint-disable-next-line
    }, [tFilters.start_date, tFilters.end_date]);

    const checkIfFiltersAreValid = () => {
        if (filterErrors.length) return false;

        if ((tFilters.start_date && !tFilters.end_date) || (!tFilters.start_date && tFilters.end_date)) {
            return false;
        };

        if (tFilters.start_date && tFilters.end_date) {
            const cStart = new Date(tFilters.start_date);
            const cEnd = new Date(tFilters.end_date);
            if (cStart > cEnd) {
                return false;
            };
        };

        if (JSON.stringify(tFilters) === JSON.stringify(filters)) {
            return false;
        };

        return true;
    };

    const onApplyFilters = () => {
        if (!checkIfFiltersAreValid()) return;

        setFilters(tFilters);

        onFiltersClose();
    };

    const onClearFilters = () => {
        if (!tFilters.start_date && !tFilters.end_date && !tFilters.scores.length) return;

        setFilterErrors([]);
        setTFilters({ start_date: '', end_date: '', scores: [], simulation_keys: [] });
        setFilters({ start_date: '', end_date: '', scores: [], simulation_keys: [] });

        onFiltersClose();
    };

    const onScoreChange = (e: MouseEvent<HTMLButtonElement>, score: number) => {
        if (tFilters.scores.includes(score)) {
            setTFilters(prev => ({ ...prev, scores: prev.scores.filter(s => s !== score) }));
        } else {
            setTFilters(prev => ({ ...prev, scores: [...prev.scores, score] }));
        };
    };

    const onSimulationKeyChange = (e: MouseEvent<HTMLButtonElement>, key: ESimulationKey) => {
        if (tFilters.simulation_keys.includes(key)) {
            setTFilters(prev => ({ ...prev, simulation_keys: prev.simulation_keys.filter(k => k !== key) }));
        } else {
            setTFilters(prev => ({ ...prev, simulation_keys: [...prev.simulation_keys, key] }));
        };
    };

    const areFiltersApplied = () => {
        return !!filters.start_date || !!filters.end_date || !!filters.scores.length || !!filters.simulation_keys.length;
    };

    const areTFiltersApplied = () => {
        return !!tFilters.start_date || !!tFilters.end_date || !!tFilters.scores.length || !!tFilters.simulation_keys.length;
    };

    return (
        <Fragment>
            <button
                className={`cd-btn cd-btn--md ${areFiltersApplied() ? 'cd-btn--active' : ''}`}
                onClick={onFiltersOpen}
            >

                {areFiltersApplied() ? <SlidersHorizontal /> : <Funnel />}
                <span>Filters</span>
            </button>
            <div className={`complete_data__filters ${filtersOpen ? 'complete_data__filters--open' : ''}`}>
                <div className='card card--md complete_data__filters__content'>
                    <div className='complete_data__filters__header'>
                        <button
                            className='cd-btn cd-btn--sm'
                            onClick={onClearFilters}
                            disabled={!areTFiltersApplied()}
                        >
                            Clear all
                        </button>
                        <button
                            className='cd-btn cd-btn--sm'
                            onClick={onApplyFilters}
                            disabled={!checkIfFiltersAreValid()}
                        >
                            Apply Filters
                        </button>
                        <button
                            className='complete_data__filters__header__close_btn'
                            onClick={onFiltersClose}
                        >
                            <X />
                        </button>
                    </div>

                    {filterErrors.length > 0 && (
                        <div className='complete_data__filters__error_section'>
                            <p className='color--error text-medium'>Invalid filters!</p>
                            <ul>
                                {filterErrors.map((error) => (
                                    <li key={error} className='color--error text-sm'>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className='complete_data__filters__date_section'>
                        <div className='d-flex justify-content-between align-items-center mb-md'>
                            <p className='text-sm text-medium color--primary'>Select the date range to include:</p>
                            <button
                                className='cd-btn cd-btn--sm'
                                onClick={() => setTFilters(prev => ({ ...prev, start_date: '', end_date: '' }))}
                                disabled={!tFilters.start_date && !tFilters.end_date}
                            >
                                Clear
                            </button>
                        </div>
                        <div className='complete_data__filters__date_section__date_inputs'>
                            <div className='complete_data__filters__date_section__date_input'>
                                <label htmlFor='id__filters_start_date'>Start Date</label>
                                <input
                                    type='date'
                                    id='id__filters_start_date'
                                    name='start_date'
                                    value={tFilters.start_date || ''}
                                    onChange={onDateChange}
                                />
                            </div>
                            <div className='complete_data__filters__date_section__date_input'>
                                <label htmlFor='id__filters_end_date'>End Date</label>
                                <input
                                    type='date'
                                    id='id__filters_end_date'
                                    name='end_date'
                                    value={tFilters.end_date || ''}
                                    onChange={onDateChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='complete_data__filters__score_section'>
                        <div className='d-flex justify-content-between align-items-center mb-md'>
                            <p className='text-sm text-medium color--primary'>Select the scores to include:</p>
                            <button
                                className='cd-btn cd-btn--sm'
                                onClick={() => setTFilters(prev => ({ ...prev, scores: [] }))}
                                disabled={!tFilters.scores.length}
                            >
                                Clear
                            </button>
                        </div>
                        <div className='d-flex justify-content-start align-items-center gap-sm'>
                            {Array.from({ length: 5 }, (_, index) => (
                                <button
                                    key={index}
                                    className={`cd-btn cd-btn--md ${tFilters.scores.includes(index + 1) ? 'cd-btn--active' : ''}`}
                                    onClick={e => onScoreChange(e, index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='complete_data__filters__scenario_section'>
                        <div className='d-flex justify-content-between align-items-center mb-md'>
                            <p className='text-sm text-medium color--primary'>Select the scenarios to include:</p>
                            <button
                                className='cd-btn cd-btn--sm'
                                onClick={() => setTFilters(prev => ({ ...prev, simulation_keys: [] }))}
                                disabled={!tFilters.simulation_keys.length}
                            >
                                Clear
                            </button>
                        </div>
                        <div className='complete_data__filters__scenario_section__scenarios'>
                            {simulations.map(({ key, name }) => (
                                <button
                                    key={key}
                                    className={`cd-btn cd-btn--md ${tFilters.simulation_keys.includes(key) ? 'cd-btn--active' : ''}`}
                                    onClick={e => onSimulationKeyChange(e, key)}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};